import React, { useEffect, useMemo, useState } from 'react';
import { ClipboardCheck, Search, RefreshCw, CheckCircle2, Calendar, History, ShieldOff, ShieldCheck, XCircle, Ban, Clock, FileText, MailPlus } from 'lucide-react';
import { toast } from 'sonner';
import { ConfirmActionDialog } from '../../components/admin/ConfirmActionDialog';
import { ApprovaRegistrazioneDialog } from '../../components/admin/ApprovaRegistrazioneDialog';
import { Pagination } from '../../components/admin/Pagination';
import { ClientActionsMenu, ClientActionsMenuItem } from '../../components/admin/ClientActionsMenu';
import { ClientCardModal } from '../../components/admin/ClientCardModal';
import { ChangeEmailCard } from '../../components/admin/ClientAccountCards';
import { adminUserService, AdminUserListItem, AdminApprovalLogItem } from '../../services/adminUserService';
import { getErrorMessage } from '../../utils/errorMessage';
import { ETICHETTA_CONTATTO } from '../../utils/contatto';

type View = 'lista' | 'history';
type StatoFiltro = '' | 'pending' | 'approved' | 'rejected';

const STATO_BADGE: Record<'pending' | 'approved' | 'rejected', { label: string; className: string; Icon: any }> = {
  pending:  { label: 'In attesa', className: 'bg-amber-100 text-amber-700',   Icon: Clock },
  approved: { label: 'Approvato', className: 'bg-emerald-100 text-emerald-700', Icon: CheckCircle2 },
  rejected: { label: 'Respinto',  className: 'bg-red-100 text-red-700',       Icon: Ban },
};

function statoOf(u: AdminUserListItem): 'pending' | 'approved' | 'rejected' {
  if (u.attivo) return 'approved';
  if (u.rejected) return 'rejected';
  return 'pending';
}

const PAGE_SIZE = 15;
const HISTORY_PAGE_SIZE = 20;

// Stessa mappa valore→etichetta della select "Parco macchine stimato" in
// RegisterPage.tsx (PARCO_OPTIONS) — qui solo per la visualizzazione admin.
const PARCO_LABEL: Record<string, string> = {
  '<50': 'Meno di 50',
  '50-200': 'Da 50 a 200',
  '200-500': 'Da 200 a 500',
  '500+': 'Oltre 500',
};

type PendingAction = { type: 'approve' | 'reject' | 'revoke'; user: AdminUserListItem } | null;

function fmtDate(iso?: string | null) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' }); }
  catch { return '—'; }
}

export default function AdminRegistrationsPage() {
  const [view, setView] = useState<View>('lista');
  const [stato, setStato] = useState<StatoFiltro>('pending');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<AdminUserListItem[]>([]);
  const [historyItems, setHistoryItems] = useState<AdminApprovalLogItem[]>([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<PendingAction>(null);
  const [actingId, setActingId] = useState<string | null>(null);
  const [emailModalUser, setEmailModalUser] = useState<AdminUserListItem | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => { setPage(1); }, [debouncedSearch, stato, view]);

  const requestIdRef = React.useRef(0);

  const load = () => {
    const requestId = ++requestIdRef.current;
    setIsLoading(true);
    setError(null);
    const applyIfCurrent = (fn: () => void) => { if (requestId === requestIdRef.current) fn(); };

    if (view === 'history') {
      adminUserService
        .approvalLog({ page, limit: HISTORY_PAGE_SIZE })
        .then((res) => applyIfCurrent(() => { setHistoryItems(res.items); setTotal(res.total); setPages(res.pages); }))
        .catch(() => applyIfCurrent(() => { setError('Impossibile caricare lo storico approvazioni.'); setHistoryItems([]); }))
        .finally(() => applyIfCurrent(() => setIsLoading(false)));
      return;
    }

    const opts: Parameters<typeof adminUserService.list>[0] = { q: debouncedSearch || undefined, page, limit: PAGE_SIZE };
    if (stato === 'pending') { opts.attivo = false; opts.rejected = false; }
    else if (stato === 'approved') { opts.attivo = true; }
    else if (stato === 'rejected') { opts.rejected = true; }
    adminUserService
      .list(opts)
      .then((res) => applyIfCurrent(() => { setItems(res.items); setTotal(res.total); setPages(res.pages); }))
      .catch((err: any) => applyIfCurrent(() => {
        const status = err?.response?.status;
        setError(status === 403 ? 'Accesso negato — ruolo admin richiesto.' : 'Impossibile caricare le registrazioni.');
        setItems([]);
      }))
      .finally(() => applyIfCurrent(() => setIsLoading(false)));
  };

  useEffect(() => { load(); }, [view, stato, debouncedSearch, page]);

  const visibleRange = useMemo(() => {
    const size = view === 'history' ? HISTORY_PAGE_SIZE : PAGE_SIZE;
    if (!total) return '0–0';
    const start = (page - 1) * size + 1;
    const end = Math.min(total, page * size);
    return `${start}–${end}`;
  }, [page, total, view]);

  const togliDallaLista = (user: AdminUserListItem) => {
    setItems((prev) => prev.filter((u) => u.userId !== user.userId));
    setTotal((t) => Math.max(0, t - 1));
  };

  // L'approvazione ha un dialogo suo (Partita IVA già nel gestionale):
  // qui passano rifiuto e revoca.
  const handleConfirm = async () => {
    if (!pendingAction) return;
    const { type, user } = pendingAction;
    setActingId(user.userId);
    try {
      if (type === 'reject') {
        await adminUserService.reject(user.userId);
        toast.success(`Registrazione di ${user.ragioneSociale || user.email} respinta.`);
      } else {
        await adminUserService.revoke(user.userId);
        toast.success(`Approvazione revocata per ${user.ragioneSociale || user.email}.`);
      }
      togliDallaLista(user);
    } catch (err: any) {
      const verbo = type === 'reject' ? 'il rifiuto' : 'la revoca';
      toast.error(getErrorMessage(err, `Errore durante ${verbo}.`));
    } finally {
      setActingId(null);
      setPendingAction(null);
    }
  };

  return (
    <div className="p-6" data-testid="admin-registrations-page">
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <ClipboardCheck className="w-5 h-5 text-[hsl(206,54%,20%)]" /> Registrazioni Utenti
          </h1>
          <p className="text-xs text-muted-foreground mt-1">
            Nuovi account creati da /registrati — approva l'accesso all'area clienti.
          </p>
        </div>
        <button
          onClick={load}
          disabled={isLoading}
          data-testid="registrations-refresh-btn"
          className="h-9 px-3 rounded-lg border text-sm font-medium flex items-center gap-1.5 hover:bg-secondary/60 transition-colors disabled:opacity-40"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${isLoading ? 'animate-spin' : ''}`} /> Aggiorna
        </button>
      </div>

      <div className="flex items-center gap-1 mb-4 border-b" data-testid="registrations-view-tabs">
        {([
          { key: 'lista' as View, label: 'Registrazioni', icon: ClipboardCheck },
          { key: 'history' as View, label: 'Storico Approvazioni', icon: History },
        ]).map((t) => (
          <button
            key={t.key}
            onClick={() => setView(t.key)}
            data-testid={`registrations-view-${t.key}`}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-1.5 ${
              view === t.key ? 'border-red-500 text-[hsl(206,54%,20%)]' : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>

      {view === 'lista' && (
        <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="registrations-stato-filter">
          {([
            { key: '' as StatoFiltro, label: 'Tutti' },
            { key: 'pending' as StatoFiltro, label: 'In attesa' },
            { key: 'approved' as StatoFiltro, label: 'Approvati' },
            { key: 'rejected' as StatoFiltro, label: 'Respinti' },
          ]).map((s) => (
            <button
              key={s.key || 'tutti'}
              onClick={() => setStato(s.key)}
              data-testid={`registrations-stato-pill-${s.key || 'tutti'}`}
              className={`h-8 px-3 rounded-full text-xs font-medium border transition-colors ${
                stato === s.key ? 'bg-[hsl(206,54%,20%)] text-white border-transparent' : 'bg-white text-muted-foreground hover:bg-secondary/60'
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
      )}

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        {view === 'lista' && (
          <div className="relative max-w-md flex-1 min-w-[250px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cerca per ragione sociale, email, P.IVA, città…"
              data-testid="registrations-search"
              className="w-full h-9 pl-9 pr-3 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-red-400"
            />
          </div>
        )}
        <span className="text-xs text-muted-foreground" data-testid="registrations-counter">
          {isLoading ? 'Caricamento…' : `${total} risultati · mostrando ${visibleRange}`}
        </span>
      </div>

      {error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-center text-sm text-red-700" data-testid="registrations-error">
          {error}
        </div>
      ) : view === 'history' ? (
        historyItems.length === 0 && !isLoading ? (
          <div className="rounded-xl border bg-white p-12 text-center text-sm text-muted-foreground" data-testid="registrations-empty">
            Nessuna approvazione registrata finora.
          </div>
        ) : (
          <div className="rounded-xl border bg-white overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-[hsl(var(--dtk-canvas))]">
                  <th className="text-left px-4 py-3 text-[10px] font-semibold text-muted-foreground uppercase">Data / Ora</th>
                  <th className="text-left px-4 py-3 text-[10px] font-semibold text-muted-foreground uppercase">Azione</th>
                  <th className="text-left px-4 py-3 text-[10px] font-semibold text-muted-foreground uppercase">Cliente</th>
                  <th className="text-left px-4 py-3 text-[10px] font-semibold text-muted-foreground uppercase">Email</th>
                  <th className="text-left px-4 py-3 text-[10px] font-semibold text-muted-foreground uppercase">Eseguita da (Admin)</th>
                </tr>
              </thead>
              <tbody>
                {historyItems.map((h, i) => {
                  const cfg = h.action === 'revoked'
                    ? { className: 'bg-red-100 text-red-700', Icon: ShieldOff, label: 'Revocato' }
                    : h.action === 'rejected'
                    ? { className: 'bg-red-100 text-red-700', Icon: Ban, label: 'Respinto' }
                    : { className: 'bg-emerald-100 text-emerald-700', Icon: ShieldCheck, label: 'Approvato' };
                  return (
                    <tr key={`${h.userId}-${h.approvedAt}-${i}`} className="border-b hover:bg-secondary/40 transition-colors" data-testid={`history-row-${h.userId}`}>
                      <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">{new Date(h.approvedAt).toLocaleString('it-IT')}</td>
                      <td className="px-4 py-3">
                        <span className={`text-[10px] px-2 py-0.5 rounded font-medium flex items-center gap-1 w-fit ${cfg.className}`} data-testid={`history-action-${h.userId}`}>
                          <cfg.Icon className="w-3 h-3" />{cfg.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-medium">{h.ragioneSociale || h.email}</td>
                      <td className="px-4 py-3 text-muted-foreground text-xs">{h.email}</td>
                      <td className="px-4 py-3">
                        <span className="text-xs font-medium text-[hsl(206,54%,20%)] bg-secondary/60 px-2 py-0.5 rounded">{h.approvedByEmail}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <Pagination page={page} pages={pages} isLoading={isLoading} onChange={setPage} testIdPrefix="registrations-history" />
          </div>
        )
      ) : items.length === 0 && !isLoading ? (
        <div className="rounded-xl border bg-white p-12 text-center text-sm text-muted-foreground" data-testid="registrations-empty">
          {stato === 'pending' ? 'Nessuna registrazione in attesa di approvazione.' : 'Nessuna registrazione trovata.'}
        </div>
      ) : (
        <div className="rounded-xl border bg-white overflow-hidden">
          <div className="overflow-auto max-h-[65vh] table-scroll-visible" data-testid="registrations-table-scroll">
            <table className="w-full" style={{ minWidth: 1700 }} data-testid="registrations-table">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left px-4 py-3 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap sticky left-0 top-0 z-30 bg-gray-50 border-r border-gray-200" style={{ width: 220 }}>Ragione Sociale</th>
                  <th className="text-left px-4 py-3 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap sticky top-0 z-20 bg-gray-50">Nominativo</th>
                  <th className="text-left px-4 py-3 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap sticky top-0 z-20 bg-gray-50">Indirizzo</th>
                  <th className="text-left px-4 py-3 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap sticky top-0 z-20 bg-gray-50">Città</th>
                  <th className="text-left px-4 py-3 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap sticky top-0 z-20 bg-gray-50">Prov.</th>
                  <th className="text-left px-4 py-3 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap sticky top-0 z-20 bg-gray-50">Email</th>
                  <th className="text-left px-4 py-3 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap sticky top-0 z-20 bg-gray-50">Telefono</th>
                  <th className="text-left px-4 py-3 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap sticky top-0 z-20 bg-gray-50">Cellulare</th>
                  <th className="text-left px-4 py-3 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap sticky top-0 z-20 bg-gray-50">Marchi preferiti</th>
                  <th className="text-left px-4 py-3 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap sticky top-0 z-20 bg-gray-50">Parco macchine</th>
                  <th className="text-left px-4 py-3 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap sticky top-0 z-20 bg-gray-50">Data Reg.</th>
                  <th className="text-left px-4 py-3 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap sticky top-0 z-20 bg-gray-50">Stato</th>
                  <th className="text-right px-4 py-3 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide w-14 sticky right-0 top-0 z-30 bg-gray-50 border-l border-gray-200">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {items.map((u) => {
                  const s = statoOf(u);
                  const badge = STATO_BADGE[s];
                  const marchi = u.marcheInteresse || [];
                  const parcoLabel = u.parcoMacchineStimato ? (PARCO_LABEL[u.parcoMacchineStimato] || u.parcoMacchineStimato) : null;
                  return (
                    <tr key={u.userId} className="border-b last:border-b-0 hover:bg-gray-50/60 transition-colors group" data-testid={`registration-row-${u.userId}`}>
                      <td className="px-4 py-3 font-medium whitespace-nowrap sticky left-0 z-10 bg-white group-hover:bg-gray-50 border-r border-gray-200 relative after:content-[''] after:absolute after:top-0 after:bottom-0 after:right-0 after:translate-x-full after:w-2 after:pointer-events-none after:[background:linear-gradient(to_right,rgba(0,0,0,0.10),rgba(0,0,0,0))]" style={{ width: 220 }} data-testid={`registration-company-${u.userId}`}>
                        {u.ragioneSociale || '—'}
                        {u.partitaIva && <div className="text-[10px] text-muted-foreground font-normal">P.IVA {u.partitaIva}</div>}
                      </td>
                      <td className="px-4 py-3 text-sm whitespace-nowrap">{u.nome} {u.cognome}</td>
                      <td className="px-4 py-3 text-sm whitespace-nowrap max-w-[200px] truncate" title={u.indirizzo || ''}>{u.indirizzo || '—'}</td>
                      <td className="px-4 py-3 text-sm whitespace-nowrap">{u.citta || '—'}</td>
                      <td className="px-4 py-3 text-sm whitespace-nowrap">{u.provincia || '—'}</td>
                      <td className="px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">{u.email}</td>
                      <td className="px-4 py-3 text-sm whitespace-nowrap">{u.telefono || '—'}</td>
                      <td className="px-4 py-3 text-sm whitespace-nowrap">{u.cellulare || '—'}</td>
                      <td className="px-4 py-3 text-sm whitespace-nowrap max-w-[180px] truncate" title={marchi.join(', ')} data-testid={`registration-marche-${u.userId}`}>
                        {marchi.length ? marchi.join(', ') : '—'}
                      </td>
                      <td className="px-4 py-3 text-sm whitespace-nowrap" data-testid={`registration-macchine-${u.userId}`}>
                        {parcoLabel ? (
                          <span className="inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded bg-secondary/60 text-foreground w-fit">
                            {parcoLabel}
                          </span>
                        ) : '—'}
                      </td>
                      <td className="px-4 py-3 text-sm whitespace-nowrap">
                        <span className="flex items-center gap-1 text-xs text-muted-foreground"><Calendar className="w-3 h-3" /> {fmtDate(u.createdAt)}</span>
                      </td>
                      <td className="px-4 py-3 text-sm whitespace-nowrap">
                        <span className={`text-[10px] px-2 py-0.5 rounded font-medium flex items-center gap-1 w-fit ${badge.className}`} data-testid={`registration-status-${u.userId}`}>
                          <badge.Icon className="w-3 h-3" />{badge.label}
                        </span>
                        {/* Approvato senza Cliente NTS: nasce al primo ordine. */}
                        {s === 'approved' && u.contatto && (
                          <span className="mt-1 text-[10px] px-2 py-0.5 rounded font-medium w-fit block bg-sky-100 text-sky-700" data-testid={`registration-contatto-${u.userId}`}>
                            {ETICHETTA_CONTATTO}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right sticky right-0 z-10 bg-white group-hover:bg-gray-50 border-l border-gray-200 relative after:content-[''] after:absolute after:top-0 after:bottom-0 after:left-0 after:-translate-x-full after:w-2 after:pointer-events-none after:[background:linear-gradient(to_left,rgba(0,0,0,0.10),rgba(0,0,0,0))]">
                        <ClientActionsMenu testId={`registration-actions-btn-${u.userId}`}>
                          {s !== 'approved' && (
                            <ClientActionsMenuItem
                              onClick={() => setPendingAction({ type: 'approve', user: u })}
                              testId={`registration-menu-approva-${u.userId}`}
                            >
                              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> Approva
                            </ClientActionsMenuItem>
                          )}
                          {s === 'pending' && (
                            <ClientActionsMenuItem
                              onClick={() => setPendingAction({ type: 'reject', user: u })}
                              testId={`registration-menu-respingi-${u.userId}`}
                            >
                              <Ban className="w-3.5 h-3.5 text-red-600" /> Respingi
                            </ClientActionsMenuItem>
                          )}
                          <ClientActionsMenuItem to={`/admin/registrazioni/${u.userId}`} testId={`registration-menu-scheda-${u.userId}`}>
                            <FileText className="w-3.5 h-3.5" /> Scheda Utente
                          </ClientActionsMenuItem>
                          <ClientActionsMenuItem onClick={() => setEmailModalUser(u)} testId={`registration-menu-cambio-mail-${u.userId}`}>
                            <MailPlus className="w-3.5 h-3.5" /> Cambio Mail
                          </ClientActionsMenuItem>
                          {s === 'approved' && !u.hasOrders && (
                            <ClientActionsMenuItem
                              onClick={() => setPendingAction({ type: 'revoke', user: u })}
                              testId={`registration-menu-revoca-${u.userId}`}
                            >
                              <ShieldOff className="w-3.5 h-3.5 text-red-600" /> Revoca approvazione
                            </ClientActionsMenuItem>
                          )}
                        </ClientActionsMenu>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Pagination page={page} pages={pages} isLoading={isLoading} onChange={setPage} testIdPrefix="registrations" />
        </div>
      )}

      <ClientCardModal open={!!emailModalUser} onClose={() => setEmailModalUser(null)}>
        {emailModalUser && (
          <ChangeEmailCard
            clientKey={emailModalUser.userId}
            currentEmail={emailModalUser.email}
            onSuccess={() => { setEmailModalUser(null); load(); }}
          />
        )}
      </ClientCardModal>

      <ApprovaRegistrazioneDialog
        user={pendingAction?.type === 'approve' ? pendingAction.user : null}
        onClose={() => setPendingAction(null)}
        onApproved={(user) => { togliDallaLista(user); setPendingAction(null); }}
      />

      <ConfirmActionDialog
        open={!!pendingAction && pendingAction.type !== 'approve'}
        onOpenChange={(open) => !open && setPendingAction(null)}
        title={pendingAction?.type === 'reject' ? 'Respingi Registrazione' : 'Revoca Approvazione'}
        description={
          pendingAction?.type === 'reject'
            ? `Confermi il rifiuto della registrazione di ${pendingAction?.user.ragioneSociale || pendingAction?.user.email}? L'account resterà a sistema come "Respinto" (email bloccata per una nuova registrazione) ma potrà comunque essere approvato in seguito se necessario. L'azione verrà registrata nello storico.`
            : `Confermi la revoca dell'approvazione per ${pendingAction?.user.ragioneSociale || pendingAction?.user.email}? L'utente non potrà più accedere all'area clienti. Consentito solo perché non ha ancora effettuato ordini sul gestionale ERP. L'azione verrà registrata nello storico.`
        }
        confirmLabel={pendingAction?.type === 'reject' ? 'Respingi' : 'Revoca'}
        variant="destructive"
        onConfirm={handleConfirm}
      />
    </div>
  );
}
